import { useContext, useRef, useState } from "react";

// Cesium
import ScreenSpaceEventType from "cesium/Source/Core/ScreenSpaceEventType";
import {
  getCartographic,
  radianToDegrees
} from "utils/Cesium/CesiumCoordinatesUtils";
import { getCesiumTooltip } from "utils/Cesium/CesiumUtils";

// Context
import { Map3DContext } from "Map3DProvider";

/**
 * Hook options for usePickPoint3D.
 * @typedef {Object} UsePickPoint3DOptions
 * @property {function({type: "Point", coordinates: number[]}): void} [onEnd] - Called on each completed point pick with [lon, lat] in degrees.
 * @property {function(): void} [onCancel] - Called when the LEFT_CLICK handler is removed via stopPickPoint.
 * @property {function(): string} [getHelpMessage] - Returns default help message shown during MOUSE_MOVE.
 */

/**
 * @typedef {Object} StartPickPoint3DOptions
 * @property {string} [helpMessage] - Optional one-off help message override for this session.
 */

/**
 * Picks a point on the 3D Cesium viewer using a LEFT_CLICK screen space event.
 * Uses project utilities to convert the picked Cartesian position to geographic coordinates.
 * Keeps the handler active for multiple clicks and only stops when explicitly cancelled.
 *
 * Encapsulates Cesium low-level lifecycle (restore handlers, tooltip rendering, cursor management,
 * right click cancellation) so consumers only orchestrate start/stop and react to callbacks.
 *
 * @param {UsePickPoint3DOptions} [options] - Optional callbacks for end/cancel and help text provider.
 * @returns {{
 *   point: {type: "Point", coordinates: number[]} | null,
 *   isActive: boolean,
 *   startPickPoint: function(StartPickPoint3DOptions=): {started: boolean, reason?: string},
 *   stopPickPoint: function(): void,
 *   clearPoint: function(): void
 * }}
 */
function usePickPoint3D({ onEnd, onCancel, getHelpMessage } = {}) {
  const map3DContext = useContext(Map3DContext);
  const viewer = map3DContext?.viewer;

  const [point, setPoint] = useState(null);
  const [isActive, setIsActive] = useState(false);

  const listenerActiveRef = useRef(false);
  const helpMessageRef = useRef("");

  function _clearCesiumInteractionArtifacts() {
    // Always try to hide tooltip, even if viewer is temporarily unavailable
    // during view switches.
    getCesiumTooltip();

    if (!viewer) {
      return;
    }

    const handler = viewer.screenSpaceEventHandler;
    handler.removeInputAction(ScreenSpaceEventType.MOUSE_MOVE);
    handler.removeInputAction(ScreenSpaceEventType.RIGHT_CLICK);

    viewer.container.style.cursor = "default";
    viewer.scene.requestRender();
  }

  function _endInteraction({ cancelled } = { cancelled: false }) {
    const handler = viewer?.screenSpaceEventHandler;

    if (listenerActiveRef.current && handler) {
      handler.removeInputAction(ScreenSpaceEventType.LEFT_CLICK);
    }
    listenerActiveRef.current = false;

    _clearCesiumInteractionArtifacts();
    if (handler && typeof map3DContext?.restoreMapHandlers === "function") {
      map3DContext.restoreMapHandlers(handler);
    }

    setIsActive(false);

    if (cancelled) {
      setPoint(null);
    }

    if (cancelled && typeof onCancel === "function") {
      onCancel();
    }
  }

  function _onClick(click) {
    const scene = viewer.scene;

    if (!scene.pickPositionSupported) {
      scene.requestRender(); //request a Cesium render
      return;
    }

    const cartographic = getCartographic(viewer, click.position);
    if (!cartographic) {
      scene.requestRender();
      return;
    }

    const longitude = radianToDegrees(cartographic.longitude);
    const latitude = radianToDegrees(cartographic.latitude);

    const geojsonPoint = {
      type: "Point",
      coordinates: [longitude, latitude]
    };

    setPoint(geojsonPoint);

    if (typeof onEnd === "function") {
      onEnd(geojsonPoint);
    }
    scene.requestRender();
  }

  function _onMouseMove(movement) {
    const tooltip = getCesiumTooltip();
    tooltip.style.left = movement.endPosition.x + 10 + "px";
    tooltip.style.top = movement.endPosition.y + 20 + "px";
    tooltip.style.display = "block";
    tooltip.innerHTML =
      typeof getHelpMessage === "function"
        ? _resolveHelpMessage()
        : helpMessageRef.current;
    viewer.scene.requestRender();
  }

  function _onRightClick() {
    _endInteraction({ cancelled: true });
  }

  function _resolveHelpMessage(helpMessage) {
    if (typeof helpMessage === "string") {
      return helpMessage;
    }
    if (typeof getHelpMessage === "function") {
      return getHelpMessage();
    }
    return "";
  }

  /**
   * Start a pick session.
   * Returns "missing-viewer" when viewer is unavailable,
   * and "busy" when a pick session is already active.
   */
  function startPickPoint({ helpMessage } = {}) {
    if (!viewer) {
      return { started: false, reason: "missing-viewer" };
    }

    if (listenerActiveRef.current || isActive) {
      return { started: false, reason: "busy" };
    }

    const handler = viewer.screenSpaceEventHandler;
    helpMessageRef.current = _resolveHelpMessage(helpMessage);

    map3DContext.restoreMapHandlers(handler);
    viewer.container.style.cursor = "crosshair";
    handler.setInputAction(_onMouseMove, ScreenSpaceEventType.MOUSE_MOVE);
    handler.setInputAction(_onClick, ScreenSpaceEventType.LEFT_CLICK);
    handler.setInputAction(_onRightClick, ScreenSpaceEventType.RIGHT_CLICK);

    listenerActiveRef.current = true;
    setIsActive(true);
    viewer.scene.requestRender();

    return { started: true };
  }

  /**
   * Stop the active pick session.
   * Treated as cancellation: removes handlers, clears current point and calls onCancel.
   */
  function stopPickPoint() {
    _endInteraction({ cancelled: true });
  }

  /**
   * Clear only the stored point value.
   * Does not alter Cesium input action registration.
   */
  function clearPoint() {
    setPoint(null);
  }

  return { point, isActive, startPickPoint, stopPickPoint, clearPoint };
}

export default usePickPoint3D;
